/**
 * Runtime context detection utilities.
 *
 * Used to detect whether the app is running inside an Android Trusted Web Activity (TWA),
 * so we can hide ALL purchase/subscription CTAs to comply with Google Play Store policies.
 *
 * Detection priority (per Play Store compliance plan):
 *   1. PRIMARY (authoritative): document.referrer.startsWith('android-app://')
 *      Set by the TWA launcher on first navigation.
 *   2. FALLBACK 1: query string ?utm_source=twa, persisted in sessionStorage as __twa=1
 *      Useful for SPA navigations after the referrer is lost.
 *   3. FALLBACK 2: display-mode standalone + Android UA containing "; wv)" (WebView marker)
 *
 * The fallbacks ONLY apply if the primary signal is unavailable.
 */

const SESSION_KEY = "__twa";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function primarySignal(): boolean {
  if (!isBrowser()) return false;
  try {
    return document.referrer.startsWith("android-app://");
  } catch {
    return false;
  }
}

function fallbackQueryFlag(): boolean {
  if (!isBrowser()) return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("utm_source") === "twa") {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      return true;
    }
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function fallbackStandaloneAndroidWebView(): boolean {
  if (!isBrowser()) return false;
  try {
    const standalone =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches;
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isWebView = /;\s*wv\)/i.test(ua);
    return standalone && isAndroid && isWebView;
  } catch {
    return false;
  }
}

/**
 * Returns true if the app is running inside an Android TWA.
 *
 * Use this to gate ALL purchase / plan / subscription CTAs:
 *
 *   {!isAndroidTWA() && <BuyButton />}
 */
export function isAndroidTWA(): boolean {
  // Modo Play Store desativado em 15/06/2026 — fluxo voltou para site/PWA.
  // Para religar: remover o `return false` abaixo.
  return false;
  // eslint-disable-next-line no-unreachable
  if (primarySignal()) return true;
  if (fallbackQueryFlag()) return true;
  if (fallbackStandaloneAndroidWebView()) return true;
  return false;
}


/**
 * Stronger check — only the primary, authoritative signal.
 * Use when you need certainty (e.g. analytics labelling).
 */
export function isAndroidTWAConfirmed(): boolean {
  return primarySignal();
}
